import { useState } from "react";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../firebase";

// Local copy of the Solis sun mark (Auth.js defines it inline, not as a shared export).
const SunMark = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="8" fill="#F5A623"/>
    {[0,45,90,135,180,225,270,315].map((deg, i) => (
      <line key={i} x1="16" y1="4" x2="16" y2="1.5"
        stroke="#F5A623" strokeWidth="2" strokeLinecap="round"
        transform={`rotate(${deg} 16 16)`}/>
    ))}
  </svg>
);

function validateUsernameFormat(u) {
  if (!u) return null;
  if (u.length < 3) return "Must be at least 3 characters.";
  if (u.length > 20) return "Must be 20 characters or fewer.";
  if (!/^[a-z0-9_]+$/.test(u)) return "Only lowercase letters, numbers, and underscores.";
  return null;
}

function generateReferralCode(name) {
  const prefix = name.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4).padEnd(4, 'X');
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let suffix = '';
  for (let i = 0; i < 4; i++) suffix += chars[Math.floor(Math.random() * chars.length)];
  return prefix + suffix;
}

export default function Profile({ onComplete }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [customInput, setCustomInput] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [usernameError, setUsernameError] = useState(null);
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [savedReferralCode, setSavedReferralCode] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [referralError, setReferralError] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [form, setForm] = useState({
    name: "",
    username: "",
    city: "",
    bio: "",
    travelStyle: "",
    pace: "",
    food: "",
    budget: "",
    interests: [],
  });

  const totalSteps = 4;

  // Flip this to false once the beta gate is removed — the macro tracker drops
  // "Code" with no other edits.
  const betaGateEnabled = true;
  const profileTracker = betaGateEnabled ? ["Code", "Account", "Profile"] : ["Account", "Profile"];

  const stepTags = {
    1: "The basics",
    2: "Travel style",
    3: "Food & budget",
    4: "Interests",
  };

  const travelStyles = ["Adventure", "Relaxation", "Culture", "Foodie", "Party", "Backpacker", "Luxury", "Digital Nomad"];
  const paces = ["Slow & easy", "Balanced", "Fast-paced", "Spontaneous"];
  const foodOptions = ["No restrictions", "Vegetarian", "Vegan", "Halal", "Kosher", "Gluten-free", "Pescatarian"];
  const budgets = ["Budget", "Mid-range", "Luxury", "Flexible"];
  const interestOptions = ["Beaches", "Hiking", "Museums", "Nightlife", "Photography", "Cooking", "Yoga", "Surfing", "History", "Art", "Music", "Sport", "Wildlife", "Architecture", "Local markets"];

  const toggleInterest = (interest) => {
    setForm(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const addCustomInterest = () => {
    const val = customInput.trim();
    if (!val) return;
    if (form.interests.includes(val)) { setCustomInput(''); return; }
    const customCount = form.interests.filter(i => !interestOptions.includes(i)).length;
    if (customCount >= 10) { setShowCustomInput(false); setCustomInput(''); return; }
    setForm(prev => ({ ...prev, interests: [...prev.interests, val] }));
    setCustomInput('');
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleCopyReferral = async () => {
    try {
      await navigator.clipboard.writeText(`https://travelsolis.com/invite/${savedReferralCode}`);
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2000);
    } catch (err) {
      console.error("[handleCopyReferral]", err);
      setReferralError("Failed to copy link. Please try again.");
    }
  };

  const canNext = () => {
    if (step === 1) return form.name.trim() && form.city.trim() && form.username.trim() && !usernameError;
    if (step === 2) return form.travelStyle && form.pace;
    if (step === 3) return form.food && form.budget;
    return true;
  };

  const handleStep1Next = async () => {
    if (!form.username) { setUsernameError("Username is required."); return; }
    const err = validateUsernameFormat(form.username);
    if (err) { setUsernameError(err); return; }
    setUsernameChecking(true);
    try {
      const snap = await getDoc(doc(db, "usernames", form.username));
      if (snap.exists()) {
        setUsernameError("This username is already taken.");
        setUsernameChecking(false);
        return;
      }
    } catch (e) { console.error(e); }
    setUsernameChecking(false);
    setStep(s => s + 1);
  };

  const handleSave = async () => {
    if (!auth.currentUser) { setSaveError("You've been signed out. Please sign in again."); return; }
    setSaving(true);
    setSaveError("");
    try {
      let photoURL = null;

      if (photoFile) {
        setUploadingPhoto(true);
        const storageRef = ref(storage, `profilePhotos/${auth.currentUser.uid}`);
        await uploadBytes(storageRef, photoFile);
        photoURL = await getDownloadURL(storageRef);
        setUploadingPhoto(false);
      }

      const code = generateReferralCode(form.name);
      await setDoc(doc(db, "users", auth.currentUser.uid), {
        ...form,
        photoURL,
        email: auth.currentUser.email,
        points: 100,
        referralCode: code,
        createdAt: new Date(),
      });

      await setDoc(doc(db, "usernames", form.username), { uid: auth.currentUser.uid });

      // Fire-and-forget referral redemption — non-blocking, onboarding succeeds regardless
      const pendingReferralCode = localStorage.getItem('solisReferralCode');
      if (pendingReferralCode) {
        (async () => {
          try {
            if (!auth.currentUser) return;
            const idToken = await auth.currentUser.getIdToken();
            const resp = await fetch(
              'https://us-central1-solis-travel.cloudfunctions.net/redeemReferral',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + idToken },
                body: JSON.stringify({ code: pendingReferralCode }),
              }
            );
            const data = await resp.json();
            if (data.redeemed || data.reason === 'already_redeemed') {
              localStorage.removeItem('solisReferralCode');
            }
          } catch (err) {
            console.error('[redeemReferral] failed:', err);
          }
        })();
      }

      setSavedReferralCode(code);
    } catch (err) {
      console.error("Profile.handleSave failed:", err);
      setSaveError("Something went wrong saving your profile. Please try again.");
    }
    setSaving(false);
  };

  const ArrowRight = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .pr-root {
          min-height: 100vh;
          display: grid;
          grid-template-columns: 1fr 1fr;
          font-family: 'DM Sans', sans-serif;
          background: var(--sol-cream);
        }

        /* ── LEFT PANEL (navy, static) ── */
        .pr-left {
          background: var(--sol-navy);
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          padding: 48px;
          position: relative;
          overflow: hidden;
        }
        .pr-sunrise {
          position: absolute;
          left: 0; right: 0; bottom: 0;
          height: 60%;
          background: radial-gradient(ellipse 100% 75% at 50% 100%, rgba(245,166,35,0.28) 0%, transparent 70%);
          pointer-events: none;
          z-index: 0;
        }
        .pr-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          position: relative;
          z-index: 1;
        }
        .pr-logo-text {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          font-weight: 600;
          color: var(--sol-cream);
          letter-spacing: -0.02em;
        }
        .pr-hero { position: relative; z-index: 1; margin-top: auto; margin-bottom: auto; }
        .pr-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(245,166,35,0.15);
          border: 1px solid rgba(245,166,35,0.25);
          border-radius: 100px;
          padding: 5px 12px;
          font-size: 11px;
          font-weight: 500;
          color: var(--sol-amber);
          letter-spacing: 0.07em;
          text-transform: uppercase;
          margin-bottom: 20px;
        }
        .pr-badge svg { width: 13px; height: 13px; }
        .pr-headline {
          font-family: 'Playfair Display', serif;
          font-size: 42px;
          font-weight: 600;
          color: var(--sol-cream);
          line-height: 1.15;
          letter-spacing: -0.02em;
          margin-bottom: 16px;
        }
        .pr-headline em { font-style: italic; color: var(--sol-amber); }
        .pr-body {
          font-size: 15px;
          font-weight: 300;
          color: rgba(255,255,255,0.7);
          line-height: 1.65;
          max-width: 340px;
        }

        /* ── RIGHT PANEL (cream) ── */
        .pr-right {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px;
        }
        .pr-form-wrap { width: 100%; max-width: 460px; }

        /* MACRO tracker (Code / Account / Profile) */
        .pr-tracker { display: flex; margin-bottom: 28px; }
        .pr-step {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
        }
        .pr-step-dot {
          width: 30px; height: 30px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 13px; font-weight: 500;
          position: relative; z-index: 1;
        }
        .pr-step:not(:first-child)::before {
          content: '';
          position: absolute;
          top: 15px; left: -50%; right: 50%;
          height: 2px;
          background: rgba(26,23,20,0.12);
          z-index: 0;
        }
        .pr-step-active::before,
        .pr-step-complete::before { background: var(--sol-amber); }
        .pr-step-complete .pr-step-dot { background: var(--sol-amber); color: #fff; }
        .pr-step-active .pr-step-dot { background: var(--sol-navy); color: #fff; }
        .pr-step-label { font-size: 12px; margin-top: 8px; }
        .pr-step-complete .pr-step-label { color: rgba(26,23,20,0.5); }
        .pr-step-active .pr-step-label { color: var(--sol-navy); font-weight: 500; }

        /* Lightweight per-step header (pill tag + quiet count) */
        .pr-step-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 18px;
        }
        .pr-step-count { font-size: 12px; color: rgba(26,23,20,0.4); }
        .pr-tag {
          display: inline-block;
          background: rgba(245,166,35,0.12);
          color: var(--sol-amber-dk);
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 4px 10px;
          border-radius: 100px;
        }

        .pr-step-body { animation: fadeUp 0.35s ease; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }

        .pr-title {
          font-family: 'Playfair Display', serif;
          font-size: 24px;
          font-weight: 600;
          color: var(--sol-navy);
          margin-bottom: 6px;
          line-height: 1.2;
        }
        .pr-title em { font-style: italic; color: var(--sol-amber); }
        .pr-sub { font-size: 14px; color: rgba(26,23,20,0.45); font-weight: 300; margin-bottom: 24px; }

        .pr-photo-area { display: flex; flex-direction: column; align-items: center; margin-bottom: 24px; }
        .pr-avatar {
          width: 90px; height: 90px;
          border-radius: 50%;
          background: rgba(245,166,35,0.12);
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 12px;
          overflow: hidden; cursor: pointer;
          border: 3px solid #fff;
          box-shadow: 0 4px 16px rgba(26,23,20,0.1);
          position: relative;
        }
        .pr-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .pr-avatar-placeholder { font-size: 32px; }
        .pr-avatar-overlay { position: absolute; inset: 0; background: rgba(26,23,20,0.4); display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.2s; border-radius: 50%; }
        .pr-avatar:hover .pr-avatar-overlay { opacity: 1; }
        .pr-avatar-overlay-text { font-size: 11px; color: #fff; font-weight: 500; }
        .pr-photo-btn { font-size: 13px; color: var(--sol-amber-dk); font-weight: 500; background: none; border: none; cursor: pointer; font-family: 'DM Sans', sans-serif; }
        .pr-photo-input { display: none; }

        .pr-field { margin-bottom: 14px; }
        .pr-label { display: block; font-size: 12px; font-weight: 500; color: rgba(26,23,20,0.55); margin-bottom: 6px; }
        .pr-input {
          width: 100%;
          padding: 13px 16px;
          border: 2px solid rgba(26,23,20,0.12);
          border-radius: 13px;
          background: #fff;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          color: var(--sol-ink);
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .pr-input:focus { border-color: var(--sol-amber); box-shadow: 0 0 0 4px rgba(245,166,35,0.12); }
        .pr-input::placeholder { color: rgba(26,23,20,0.28); }
        .pr-textarea {
          width: 100%;
          padding: 13px 16px;
          border: 2px solid rgba(26,23,20,0.12);
          border-radius: 13px;
          background: #fff;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          color: var(--sol-ink);
          outline: none;
          resize: none;
          height: 70px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .pr-textarea:focus { border-color: var(--sol-amber); box-shadow: 0 0 0 4px rgba(245,166,35,0.12); }

        .pr-options { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
        .pr-option { padding: 8px 14px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 100px; background: #fff; font-size: 13px; color: rgba(26,23,20,0.6); cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
        .pr-option.selected { background: var(--sol-amber); color: #fff; border-color: var(--sol-amber); }
        .pr-option:hover:not(.selected) { border-color: var(--sol-amber); }

        .pr-interests { display: flex; flex-wrap: wrap; gap: 8px; }
        .pr-interest { padding: 7px 13px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 100px; background: #fff; font-size: 12px; color: rgba(26,23,20,0.6); cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
        .pr-interest.selected { background: var(--sol-amber); color: #fff; border-color: var(--sol-amber); }
        .pr-interest:hover:not(.selected) { border-color: var(--sol-amber); }
        .pr-interest-custom { padding: 7px 13px; border: 1.5px solid var(--sol-amber); border-radius: 100px; background: rgba(245,166,35,0.12); font-size: 12px; color: var(--sol-amber-dk); font-family: 'DM Sans', sans-serif; display: inline-flex; align-items: center; gap: 5px; }
        .pr-interest-custom-remove { background: none; border: none; cursor: pointer; color: var(--sol-amber-dk); font-size: 14px; line-height: 1; padding: 0; opacity: 0.6; font-family: 'DM Sans', sans-serif; }
        .pr-interest-custom-remove:hover { opacity: 1; }
        .pr-interest-add { padding: 7px 13px; border: 1.5px dashed rgba(245,166,35,0.4); border-radius: 100px; background: #fff; font-size: 12px; color: var(--sol-amber-dk); cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
        .pr-interest-add:hover { border-color: var(--sol-amber); background: rgba(245,166,35,0.12); }
        .pr-interest-input { padding: 6px 12px; border: 1.5px solid var(--sol-amber); border-radius: 100px; background: #fff; font-size: 12px; font-family: 'DM Sans', sans-serif; color: var(--sol-ink); outline: none; width: 130px; }
        .pr-interest-input:focus { border-color: var(--sol-amber-dk); }
        .pr-interest-confirm { padding: 6px 11px; border: none; border-radius: 100px; background: var(--sol-amber); color: #fff; font-size: 12px; cursor: pointer; font-family: 'DM Sans', sans-serif; }

        .pr-btn-row { display: flex; gap: 10px; margin-top: 24px; }
        .pr-btn-back { padding: 13px 20px; border: 1.5px solid rgba(26,23,20,0.12); border-radius: 13px; background: #fff; font-size: 14px; font-weight: 500; color: rgba(26,23,20,0.55); cursor: pointer; font-family: 'DM Sans', sans-serif; }
        .pr-btn-next {
          flex: 1;
          padding: 13px;
          background: var(--sol-amber);
          color: #fff;
          border: none;
          border-radius: 13px;
          font-size: 14px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          box-shadow: 0 4px 20px rgba(245,166,35,0.3);
        }
        .pr-btn-next:hover:not(:disabled) { background: var(--sol-amber-dk); transform: translateY(-1px); box-shadow: 0 6px 24px rgba(245,166,35,0.38); }
        .pr-btn-next:disabled { opacity: 0.4; cursor: not-allowed; }
        .pr-btn-next svg { width: 16px; height: 16px; }

        .pr-inline-error { font-size: 12px; color: #c9622f; margin-top: 8px; }

        .pr-referral-box { background: var(--sol-cream); border: 1.5px solid rgba(245,166,35,0.25); border-radius: 13px; padding: 14px 16px; margin-bottom: 20px; display: flex; flex-direction: column; gap: 10px; }
        .pr-referral-link { font-size: 12px; color: rgba(26,23,20,0.6); word-break: break-all; font-family: monospace; line-height: 1.5; }
        .pr-referral-copy { padding: 10px 20px; background: var(--sol-amber); color: #fff; border: none; border-radius: 10px; font-size: 13px; font-weight: 500; font-family: 'DM Sans', sans-serif; cursor: pointer; transition: background 0.2s; align-self: flex-start; }
        .pr-referral-copy:hover { background: var(--sol-amber-dk); }

        @media (max-width: 768px) {
          .pr-root { grid-template-columns: 1fr; }
          .pr-left { display: none; }
          .pr-right { padding: 32px 24px; }
          .pr-headline { font-size: 32px; }
        }
      `}</style>

      <div className="pr-root">

        {/* ── LEFT PANEL ── */}
        <div className="pr-left">
          <div className="pr-logo">
            <SunMark size={32} />
            <span className="pr-logo-text">Solis</span>
          </div>

          <div className="pr-hero">
            <div className="pr-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z"/>
                <path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/>
              </svg>
              Last step
            </div>
            <h1 className="pr-headline">Make Solis <em>yours</em>.</h1>
            <p className="pr-body">A few quick details so we can tailor trips to you.</p>
          </div>

          <div className="pr-sunrise" />
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className="pr-right">
          <div className="pr-form-wrap">

            {savedReferralCode ? (
              <div className="pr-step-body">
                <span className="pr-tag" style={{ marginBottom: 12, display: "inline-block" }}>You're all set!</span>
                <h2 className="pr-title">Share Solis with <em>friends</em></h2>
                <p className="pr-sub">Invite friends with your personal link and earn rewards.</p>
                <div className="pr-referral-box">
                  <span className="pr-referral-link">https://travelsolis.com/invite/{savedReferralCode}</span>
                  <button className="pr-referral-copy" onClick={handleCopyReferral}>
                    {referralCopied ? "✓ Copied!" : "Copy Link"}
                  </button>
                </div>
                {referralError && (
                  <p className="pr-inline-error">{referralError}</p>
                )}
                <div className="pr-btn-row">
                  <button className="pr-btn-next" onClick={onComplete}>Continue to app →</button>
                </div>
              </div>
            ) : (
              <>
                {/* MACRO tracker */}
                <div className="pr-tracker">
                  {profileTracker.map((label, i) => {
                    const status = label === "Profile" ? "active" : "complete";
                    return (
                      <div className={`pr-step pr-step-${status}`} key={label}>
                        <div className="pr-step-dot">{status === "complete" ? "✓" : i + 1}</div>
                        <span className="pr-step-label">{label}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Lightweight per-step header */}
                <div className="pr-step-head">
                  <span className="pr-tag">{stepTags[step]}</span>
                  <span className="pr-step-count">{step} of {totalSteps}</span>
                </div>

                <div className="pr-step-body" key={step}>

                  {step === 1 && (
                    <>
                      <h2 className="pr-title">Tell us about <em>yourself</em></h2>
                      <p className="pr-sub">Your profile helps the AI plan trips that suit you perfectly.</p>

                      <div className="pr-photo-area">
                        <label htmlFor="photo-upload" className="pr-avatar">
                          {photoPreview ? (
                            <img src={photoPreview} alt="Profile" />
                          ) : (
                            <span className="pr-avatar-placeholder">📷</span>
                          )}
                          <div className="pr-avatar-overlay">
                            <span className="pr-avatar-overlay-text">Change</span>
                          </div>
                        </label>
                        <input id="photo-upload" type="file" accept="image/*" className="pr-photo-input" onChange={handlePhotoChange} />
                        <button className="pr-photo-btn" onClick={() => document.getElementById('photo-upload').click()}>
                          {photoPreview ? "Change photo" : "Add profile photo"}
                        </button>
                      </div>

                      <div className="pr-field">
                        <label className="pr-label">Your name</label>
                        <input className="pr-input" type="text" placeholder="Jamie Martinez"
                          value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                      </div>
                      <div className="pr-field">
                        <label className="pr-label">Username</label>
                        <input
                          className="pr-input"
                          type="text"
                          placeholder="e.g. jamietravels"
                          maxLength={20}
                          value={form.username}
                          onChange={e => {
                            const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20);
                            setForm(f => ({...f, username: val}));
                            setUsernameError(val ? validateUsernameFormat(val) : null);
                          }}
                          style={usernameError ? {borderColor:'#c9622f'} : {}}
                        />
                        {usernameError ? (
                          <p style={{fontSize:11,color:'#c9622f',marginTop:4}}>{usernameError}</p>
                        ) : (
                          <p style={{fontSize:11,color:'rgba(26,23,20,0.4)',marginTop:4}}>3–20 characters, lowercase letters, numbers, underscores. Friends find you by username.</p>
                        )}
                      </div>
                      <div className="pr-field">
                        <label className="pr-label">Home city</label>
                        <input className="pr-input" type="text" placeholder="Los Angeles, CA"
                          value={form.city} onChange={e => setForm({...form, city: e.target.value})} />
                      </div>
                      <div className="pr-field">
                        <label className="pr-label">Bio <span style={{color:'rgba(26,23,20,0.35)'}}>— optional</span></label>
                        <textarea className="pr-textarea" placeholder="Tell fellow travelers a bit about yourself..."
                          value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} />
                      </div>
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <h2 className="pr-title">How do you <em>like to travel?</em></h2>
                      <p className="pr-sub">The AI uses this to match activities to your vibe.</p>

                      <div className="pr-field">
                        <label className="pr-label">Travel style</label>
                        <div className="pr-options">
                          {travelStyles.map(s => (
                            <button key={s} className={`pr-option ${form.travelStyle === s ? "selected" : ""}`}
                              onClick={() => setForm({...form, travelStyle: s})}>{s}</button>
                          ))}
                        </div>
                      </div>

                      <div className="pr-field">
                        <label className="pr-label">Preferred pace</label>
                        <div className="pr-options">
                          {paces.map(p => (
                            <button key={p} className={`pr-option ${form.pace === p ? "selected" : ""}`}
                              onClick={() => setForm({...form, pace: p})}>{p}</button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {step === 3 && (
                    <>
                      <h2 className="pr-title">Food and <em>budget preferences</em></h2>
                      <p className="pr-sub">So the AI can recommend restaurants that work for you.</p>

                      <div className="pr-field">
                        <label className="pr-label">Dietary preference</label>
                        <div className="pr-options">
                          {foodOptions.map(f => (
                            <button key={f} className={`pr-option ${form.food === f ? "selected" : ""}`}
                              onClick={() => setForm({...form, food: f})}>{f}</button>
                          ))}
                        </div>
                      </div>

                      <div className="pr-field">
                        <label className="pr-label">Budget range</label>
                        <div className="pr-options">
                          {budgets.map(b => (
                            <button key={b} className={`pr-option ${form.budget === b ? "selected" : ""}`}
                              onClick={() => setForm({...form, budget: b})}>{b}</button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {step === 4 && (
                    <>
                      <h2 className="pr-title">What do you <em>love doing?</em></h2>
                      <p className="pr-sub">Pick as many as you like — the more you share the better the AI plans.</p>

                      <div className="pr-interests">
                        {interestOptions.map(interest => (
                          <button key={interest}
                            className={`pr-interest ${form.interests.includes(interest) ? "selected" : ""}`}
                            onClick={() => toggleInterest(interest)}>
                            {interest}
                          </button>
                        ))}
                        {form.interests.filter(i => !interestOptions.includes(i)).map(interest => (
                          <span key={interest} className="pr-interest-custom">
                            {interest}
                            <button className="pr-interest-custom-remove" onClick={() => toggleInterest(interest)}>×</button>
                          </span>
                        ))}
                        {showCustomInput ? (
                          <>
                            <input
                              className="pr-interest-input"
                              autoFocus
                              placeholder="e.g. Pottery"
                              value={customInput}
                              onChange={e => setCustomInput(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') addCustomInterest();
                                if (e.key === 'Escape') { setShowCustomInput(false); setCustomInput(''); }
                              }}
                              maxLength={30}
                            />
                            <button className="pr-interest-confirm" onClick={addCustomInterest}>✓</button>
                          </>
                        ) : (
                          form.interests.filter(i => !interestOptions.includes(i)).length < 10 && (
                            <button className="pr-interest-add" onClick={() => setShowCustomInput(true)}>+ Add</button>
                          )
                        )}
                      </div>

                      {form.interests.length > 0 && (
                        <p style={{fontSize:12, color:'rgba(26,23,20,0.45)', marginTop:12}}>
                          {form.interests.length} selected ✓
                        </p>
                      )}
                    </>
                  )}

                  <div className="pr-btn-row">
                    {step > 1 && (
                      <button className="pr-btn-back" onClick={() => setStep(step - 1)}>← Back</button>
                    )}
                    {step < totalSteps ? (
                      <button
                        className="pr-btn-next"
                        disabled={!canNext() || usernameChecking}
                        onClick={step === 1 ? handleStep1Next : () => setStep(s => s + 1)}
                      >
                        {usernameChecking ? "Checking..." : <>Continue <ArrowRight /></>}
                      </button>
                    ) : (
                      <button className="pr-btn-next" onClick={handleSave} disabled={saving}>
                        {saving ? (uploadingPhoto ? "Uploading photo..." : "Saving...") : "Let's go 🌍"}
                      </button>
                    )}
                  </div>
                  {saveError && <p className="pr-inline-error">{saveError}</p>}

                </div>
              </>
            )}

          </div>
        </div>

      </div>
    </>
  );
}
